import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useUserContext } from '@blocs.auth';
import { captureExceptionEvent } from '@blocs.observability';
import { ResumeSubscriptionMutation, ResumeSubscriptionDoc } from '@minecraft.graphql-operations';
import { gqlSubscriptionStatusEnum } from '@minecraft.graphql-types';

type useResumeSubscriptionReturn = {
  resumeSubscription: () => Promise<ResumeSubscriptionMutation['resumeSubscription'] | undefined>;
  resumeSubscriptionData: ResumeSubscriptionMutation['resumeSubscription'] | undefined;
  resumeSubscriptionMutationLoading: boolean;
  resumeSubscriptionError: Error | undefined;
  isOpen: boolean;
  isPausedSubscription: boolean;
  isLoading: boolean;
  setOpen: (open: boolean) => void;
};

export const useResumeSubscription = (): useResumeSubscriptionReturn => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resumeSubscriptionData, setResumeSubscriptionData] = useState<
    ResumeSubscriptionMutation['resumeSubscription'] | undefined
  >(undefined);
  const { subscription, refreshTokens } = useUserContext();
  const isPausedSubscription = subscription?.status === gqlSubscriptionStatusEnum.PAUSED;
  const setOpen = useCallback((open: boolean) => setIsOpen(open), []);

  const [resumeSubscriptionMutation, { loading: resumeSubscriptionMutationLoading, error: resumeSubscriptionError }] =
    useMutation(ResumeSubscriptionDoc, {
      update: (cache, { data }) => {
        if (!data?.resumeSubscription) {
          return;
        }
        cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'artistSubscriptions',
        });
        cache.gc();
      },
    });

  const resumeSubscription = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await resumeSubscriptionMutation({
        variables: { input: { subscriptionId: Number(subscription?.id) } },
      });
      await refreshTokens();
      setResumeSubscriptionData(response.data.resumeSubscription);
      return response.data.resumeSubscription;
    } catch (err) {
      captureExceptionEvent(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [refreshTokens, resumeSubscriptionMutation, subscription?.id]);

  return {
    setOpen,
    isOpen,
    isLoading,
    resumeSubscription,
    resumeSubscriptionData,
    resumeSubscriptionMutationLoading,
    resumeSubscriptionError,
    isPausedSubscription,
  };
};
